import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { PublicHeader } from '../../components/shell/PublicHeader';
import { PublicFooter } from '../../components/shell/PublicFooter';
import { SEO } from '../../components/SEO';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, 
  ArrowRight,
  CheckSquare, 
  Download,
  Link2,
  Printer,
  RotateCcw,
  Save,
  Zap
} from 'lucide-react';
import { Button } from '../../components/ui';

import { TOOLS_LIST, ToolItem } from './ToolsDirectoryPage';
import { ToolRenderer } from '../../components/tools/ToolRenderer';
import { ToolClaimOrgModal } from '../../components/tools/common/ToolClaimOrgModal';
import { ToolExportFollowUp } from '../../components/tools/ToolExportFollowUp';
import { ToolResultCta } from '../../components/tools/ToolResultCta';
import { toolsSeoData } from './seoData';
import { toolFaqs } from './faqData';
import { useCurrentUser } from '../../contexts/AuthContext';
import { getToolCtaCopy, getToolSegment } from '../../lib/toolCopy';
import { TOOL_EVENTS, track } from '../../lib/analytics';
import {
  captureToolFields,
  clearPendingToolDraft,
  clearToolDraft,
  decodeToolDraft,
  encodeToolDraft,
  ensurePendingToolDraft,
  getPendingToolDraft,
  loadToolDraft,
  restoreToolFields,
  saveToolDraft,
  type ToolDraft,
} from '../../lib/toolState';
import toast from 'react-hot-toast';

const SLUG_ALIASES: Record<string, string> = {
  'board-meeting-agenda-builder': 'meeting-agenda-template',
  'board-agenda-maker': 'meeting-agenda-template',
  'board-agenda-creator': 'meeting-agenda-template',
  'online-board-agenda-maker': 'meeting-agenda-template',
  'volunteer-hours-verification': 'volunteer-hour-verification',
  'volunteer-hour-verification-system': 'volunteer-hour-verification',
  'hoa-budgeting-tools': 'hoa-budget-tracker',
  'association-budgeting-tools': 'hoa-budget-tracker',
  'hoa-budget-reserve-suite': 'hoa-budget-tracker',
  'budgeting-and-reserve-tracking-tool-hoa': 'hoa-budget-tracker',
  'reverse-raffle': 'reverse-raffle-board',
  'elimination-raffle-board': 'reverse-raffle-board',
  'reverse-elimination-raffle': 'reverse-raffle-board',
  'reverse-raffle-generator': 'reverse-raffle-board',
};

const ARCADE_GAMES = new Set([
  'deep-sea-explorer',
  'org-market-wars',
  'orgle',
  'node-man',
  'org-run',
  'micro-machines'
]);

export function ToolContainerPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const normalizedRaw = rawSlug?.replace(/\/$/, '') || '';
  const slug = SLUG_ALIASES[normalizedRaw] || normalizedRaw;
  const navigate = useNavigate();

  const tool = TOOLS_LIST.find(t => t.slug === slug);

  const seoData = tool && slug ? toolsSeoData[slug] : null;
  const adCopy = {
    title: seoData?.ctaTitle || `Stop using spreadsheets for your ${tool?.title.toLowerCase() || 'calculations'}.`,
    desc: seoData?.ctaDesc || "OrgSets provides an all-in-one platform for civic associations, non-profits, and communities to manage multi-tiered dues, automate governance compliance, and scale operations."
  };

  const { isSignedIn } = useCurrentUser();
  const segment = tool ? getToolSegment(tool.category, tool.slug) : 'operator';
  const isOperatorTool = segment === 'operator';

  // General state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showExportFollowUp, setShowExportFollowUp] = useState(false);
  const [toolKey, setToolKey] = useState(0);
  const [draftRestored, setDraftRestored] = useState(false);
  const [sharedDraftLoaded, setSharedDraftLoaded] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [resultReady, setResultReady] = useState(false);

  const toolAreaRef = useRef<HTMLDivElement>(null);
  const isRestoringRef = useRef(false);
  const sharedDraftSlugRef = useRef<string | null>(null);
  const suppressPersistRef = useRef(false);
  const interactedRef = useRef(false);
  const printSourceRef = useRef<'action_bar' | 'tool_internal'>('tool_internal');

  const [showStickyBar, setShowStickyBar] = useState(false);
  const [stickyBarDismissed, setStickyBarDismissed] = useState(() => {
    try { return sessionStorage.getItem('orgsets-sticky-bar-dismissed') === '1'; } catch { return false; }
  });

  const persistDraft = useCallback((options: {
    toast?: boolean;
    createPending?: boolean;
    reason?: string;
  } = {}): boolean => {
    if (!tool || !toolAreaRef.current || suppressPersistRef.current) return false;

    const fields = captureToolFields(toolAreaRef.current);
    if (!fields.length) return false;

    const draft: ToolDraft = {
      version: 1,
      slug: tool.slug,
      savedAt: new Date().toISOString(),
      fields,
    };
    saveToolDraft(draft);
    setHasDraft(true);
    setDraftSavedAt(draft.savedAt);

    if (options.createPending) {
      let orgName = '';
      try { orgName = localStorage.getItem('orgsets_lead_org') || ''; } catch { /* ignore */ }
      ensurePendingToolDraft(tool.slug, tool.title, orgName, 'tool');
    }

    if (options.toast) {
      toast.success('Draft saved on this device.');
      track(TOOL_EVENTS.draftSaved, {
        tool_slug: tool.slug,
        tool_segment: segment,
        reason: options.reason || 'manual',
        field_count: fields.length,
      });
    }

    return true;
  }, [tool, slug, segment]);

  // Restore a local draft or a shared "copy link with inputs" draft.
  useEffect(() => {
    if (!tool) return;

    setDraftRestored(false);
    setSharedDraftLoaded(false);
    setDraftSavedAt(null);
    setHasDraft(false);
    setResultReady(false);
    interactedRef.current = false;

    const params = new URLSearchParams(window.location.search);
    const shared = params.get('d');
    const hadResume = params.has('resume');
    let draft: ToolDraft | null = null;
    let fromShared = false;

    if (hadResume) {
      params.delete('resume');
    }

    if (shared) {
      const decoded = decodeToolDraft(shared);
      if (decoded && decoded.slug === tool.slug) {
        draft = decoded;
        fromShared = true;
        sharedDraftSlugRef.current = tool.slug;
        saveToolDraft(decoded);
      }
      params.delete('d');
    }

    if (shared || hadResume) {
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    } else if (sharedDraftSlugRef.current === tool.slug) {
      // The URL param is stripped right after it is consumed, so remember it
      // for the rest of this page view (React can run this effect twice).
      fromShared = true;
    }

    if (!draft) draft = loadToolDraft(tool.slug);
    if (!draft?.fields.length) return;

    const draftToRestore = draft;
    const expected = draftToRestore.fields.length;
    const delays = [0, 80, 220, 500, 900];
    const timers: number[] = [];
    let bestRestored = 0;
    let tracked = false;
    let cancelled = false;

    isRestoringRef.current = true;

    const restore = (attempt: number) => {
      if (cancelled) return;

      const root = toolAreaRef.current;
      if (root) {
        const restored = restoreToolFields(root, draftToRestore.fields);
        if (restored > bestRestored) bestRestored = restored;

        if (restored > 0) {
          setDraftRestored(true);
          setSharedDraftLoaded(fromShared);
          setHasDraft(true);
          setDraftSavedAt(draftToRestore.savedAt);
          setResultReady(true);

          if (!tracked) {
            tracked = true;
            track(TOOL_EVENTS.resumed, {
              tool_slug: tool.slug,
              tool_segment: segment,
              source: fromShared ? 'share_link' : 'local_draft',
              field_count: restored,
            });
          }
        }
      }

      // Some tools re-render shortly after mount, which would overwrite the
      // restored DOM values. Retry a few times, and stop as soon as every
      // field is present, so users never see a flash of defaults.
      const next = attempt + 1;
      if (bestRestored < expected && next < delays.length) {
        timers.push(window.setTimeout(() => restore(next), delays[next] - delays[attempt]));
      } else {
        isRestoringRef.current = false;
      }
    };

    restore(0);

    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
      isRestoringRef.current = false;
    };
  }, [slug, tool, toolKey, segment]);

  // Capture input changes: autosave the draft and decide when a result exists.
  useEffect(() => {
    const root = toolAreaRef.current;
    if (!root || !tool) return;

    let saveTimer: number | undefined;
    let resultTimer: number | undefined;

    const handleInput = () => {
      if (isRestoringRef.current) return;

      if (!interactedRef.current) {
        interactedRef.current = true;
        track(TOOL_EVENTS.firstInput, {
          tool_slug: tool.slug,
          tool_segment: segment,
        });
      }

      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => persistDraft({ reason: 'auto' }), 800);

      window.clearTimeout(resultTimer);
      resultTimer = window.setTimeout(() => {
        setResultReady(true);
        track(TOOL_EVENTS.resultReady, {
          tool_slug: tool.slug,
          tool_segment: segment,
        });
      }, 1200);
    };

    root.addEventListener('input', handleInput, true);
    root.addEventListener('change', handleInput, true);

    return () => {
      root.removeEventListener('input', handleInput, true);
      root.removeEventListener('change', handleInput, true);
      window.clearTimeout(saveTimer);
      window.clearTimeout(resultTimer);
      persistDraft({ reason: 'unmount' });
    };
  }, [persistDraft, tool, toolKey, segment]);

  // Export is never gated. We intercept only to save the draft and to offer an
  // optional post-export email follow-up.
  useEffect(() => {
    if (typeof window === 'undefined' || !tool) return;
    const originalPrint = window.print.bind(window);

    window.print = () => {
      persistDraft({ createPending: true, reason: 'export' });
      track(TOOL_EVENTS.exportClick, {
        tool_slug: tool.slug,
        tool_segment: segment,
        placement: printSourceRef.current,
      });
      printSourceRef.current = 'tool_internal';
      originalPrint();

      window.setTimeout(() => {
        track(TOOL_EVENTS.exportCompleted, {
          tool_slug: tool.slug,
          tool_segment: segment,
        });
        setShowExportFollowUp(true);
        track(TOOL_EVENTS.exportFollowUpView, {
          tool_slug: tool.slug,
          tool_segment: segment,
        });
      }, 250);
    };

    return () => {
      window.print = originalPrint;
    };
  }, [persistDraft, tool, segment]);

  // Intent-based sticky bar: after a result exists, not after an arbitrary 45s.
  useEffect(() => {
    if (!isOperatorTool || stickyBarDismissed) return;
    if (!resultReady && !draftRestored) return;

    const timer = window.setTimeout(() => setShowStickyBar(true), 3500);
    return () => window.clearTimeout(timer);
  }, [isOperatorTool, resultReady, draftRestored, stickyBarDismissed]);

  const dismissStickyBar = () => {
    setShowStickyBar(false);
    setStickyBarDismissed(true);
    try { sessionStorage.setItem('orgsets-sticky-bar-dismissed', '1'); } catch { /* ignore */ }
  };

  const handleExportClick = () => {
    printSourceRef.current = 'action_bar';
    window.print();
  };

  const handleSaveDraft = () => {
    const saved = persistDraft({ toast: true, createPending: true, reason: 'manual' });
    if (!saved) toast.error('Add some inputs first, then save your draft.');
  };

  const handleShareLink = async () => {
    if (!tool || !toolAreaRef.current) return;

    const fields = captureToolFields(toolAreaRef.current);
    if (!fields.length) {
      toast.error('Add some inputs first, then copy a link.');
      return;
    }

    const encoded = encodeToolDraft({
      version: 1,
      slug: tool.slug,
      savedAt: new Date().toISOString(),
      fields,
    });

    if (!encoded) {
      toast.error('This draft is too large to share by link. Save it on this device instead.');
      return;
    }

    const url = `${window.location.origin}/tools/${tool.slug}?d=${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link with your inputs copied.');
    } catch {
      window.prompt('Copy this link:', url);
    }

    track(TOOL_EVENTS.shareLinkCopied, {
      tool_slug: tool.slug,
      tool_segment: segment,
      field_count: fields.length,
    });
  };

  const handleStartFresh = () => {
    if (!tool) return;

    suppressPersistRef.current = true;
    clearToolDraft(tool.slug);
    clearPendingToolDraft();
    sharedDraftSlugRef.current = null;
    setToolKey(key => key + 1);
    setDraftRestored(false);
    setSharedDraftLoaded(false);
    setHasDraft(false);
    setDraftSavedAt(null);
    setResultReady(false);
    interactedRef.current = false;

    window.setTimeout(() => {
      suppressPersistRef.current = false;
    }, 200);

    toast.success('Draft cleared. Starting fresh.');
    track(TOOL_EVENTS.draftCleared, {
      tool_slug: tool.slug,
      tool_segment: segment,
    });
  };

  const handlePrimaryCta = (placement: string) => {
    if (!tool) return;

    persistDraft({ createPending: true, reason: 'cta' });
    track(TOOL_EVENTS.ctaClick, {
      tool_slug: tool.slug,
      tool_segment: segment,
      placement,
    });

    if (isOperatorTool) {
      setShowClaimModal(true);
    } else {
      navigate('/tools');
    }
  };

  const handlePromoSignup = () => {
    if (!tool) return;

    persistDraft({ createPending: true, reason: 'promo' });
    track(TOOL_EVENTS.ctaClick, {
      tool_slug: tool.slug,
      tool_segment: segment,
      placement: 'promo',
    });

    let email = '';
    try { email = localStorage.getItem('orgsets_lead_email') || ''; } catch { /* ignore */ }

    const params = new URLSearchParams({
      source: 'tool',
      slug: tool.slug,
      redirect_url: '/dashboard',
    });
    if (email) params.set('email', email);
    navigate(`/sign-up?${params.toString()}`);
  };

  if (!tool) {
    return (
      <div className="min-h-screen pt-28 text-center flex flex-col items-center justify-center bg-white dark:bg-[#050505] dark:text-white">
        <SEO 
          title="Tool Not Found | OrgSets" 
          description="The requested utility does not exist or has been archived." 
          noindex 
        />
        <h2 className="text-2xl font-black mb-2">Tool Not Found</h2>
        <p className="text-gray-500 mb-6">The requested utility does not exist or has been archived.</p>
        <Button onClick={() => navigate('/tools')} variant="secondary">Back to Tools</Button>
      </div>
    );
  }

  const copy = getToolCtaCopy(tool);

  const toolSchema = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": tool.title,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Any",
      "description": tool.description,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://orgsets.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Tools",
          "item": "https://orgsets.com/tools"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": tool.title,
          "item": `https://orgsets.com/tools/${slug}`
        }
      ]
    }
  ];

  const defaultFaqs = [
    {
      q: `What is the ${tool.title}?`,
      a: `The ${tool.title} is a free utility provided by OrgSets to help you ${tool.description.toLowerCase()}.`
    },
    {
      q: `Do I need an account to use the ${tool.title}?`,
      a: `No, this tool is completely free and requires no account. However, if you want to save your progress and collaborate with your team, consider trying OrgSets.`
    },
    {
      q: `How does OrgSets help with ${tool.category.toLowerCase()}?`,
      a: `OrgSets is an all-in-one platform that natively handles ${tool.category.toLowerCase()} at scale, replacing spreadsheets and ad-hoc tools.`
    }
  ];

  const faqs = slug && toolFaqs[slug] ? toolFaqs[slug] : defaultFaqs;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const pageSchemas = [...toolSchema, faqSchema];
  const isArcadeGame = Boolean(tool && ARCADE_GAMES.has(tool.slug));

  return (
    <div className="min-h-screen transition-colors duration-300 print:bg-white print:text-black font-sans bg-white text-black dark:bg-black dark:text-white">
      <SEO 
        title={seoData?.seoTitle || `${tool.title} | Free OrgSets Tool`} 
        description={seoData?.seoDescription || `${seoData?.introCopy || tool.description} Use this free tool, no signup required.`}
        keywords={seoData?.seoKeywords}
        url={`https://orgsets.com/tools/${slug}`}
        schema={pageSchemas}
        noindex={isArcadeGame}
      />
      <div className="print:hidden">
        <PublicHeader />
      </div>

      <main className="pt-28 pb-24 print:py-8">
        <div className="max-w-7xl mx-auto px-6 xl:px-12 print:max-w-none print:px-8 print:mx-0">
          {/* Back Navigation Bar */}
          <div className="flex justify-between items-center mb-8 pb-4 border-b-[4px] border-black dark:border-white print:hidden">
            <button 
              onClick={() => navigate('/tools')}
              className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-black dark:text-white hover:text-brand-primary dark:hover:text-brand-primary transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Toolbox
            </button>
            <span className="border-[2px] border-black dark:border-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-[#FFD600] text-black">{tool.category}</span>
          </div>

          {/* Intro Information */}
          <div className="space-y-4 mb-12 print:hidden">
            <h1 className="text-4xl md:text-6xl font-black font-display uppercase tracking-tighter text-black dark:text-white leading-[0.85] break-words">
              {tool.title}
            </h1>
            <h2 className="text-xl font-bold text-black dark:text-white">
              {tool.description}
            </h2>
            {seoData?.introCopy && (
              <p className="text-base font-bold text-gray-600 dark:text-gray-400 leading-relaxed max-w-4xl mt-4">
                {seoData.introCopy}
              </p>
            )}
          </div>

          {(hasDraft || draftRestored) && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-[3px] border-black dark:border-white bg-[#FFD600]/15 px-4 py-3 print:hidden">
              <div className="flex items-center gap-2 text-xs font-bold text-black dark:text-white">
                <span className="w-2.5 h-2.5 bg-emerald-500 border border-black shrink-0" />
                <span>
                  {sharedDraftLoaded
                    ? 'Shared inputs loaded.'
                    : draftRestored
                      ? 'Draft restored from your last visit.'
                      : 'Draft saved on this device.'}
                  {draftSavedAt && (
                    <span className="text-black/50 dark:text-white/50 ml-1">
                      {' '}· {new Date(draftSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleShareLink}
                  className="flex items-center gap-1.5 border-2 border-black dark:border-white bg-white dark:bg-black px-3 py-1.5 text-[10px] font-black uppercase tracking-wider hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer"
                >
                  <Link2 size={12} /> Copy Link
                </button>
                <button
                  type="button"
                  onClick={handleStartFresh}
                  className="flex items-center gap-1.5 border-2 border-black dark:border-white bg-transparent px-3 py-1.5 text-[10px] font-black uppercase tracking-wider hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors cursor-pointer"
                >
                  <RotateCcw size={12} /> Start Fresh
                </button>
              </div>
            </div>
          )}

          {/* Dynamic Interactive Components */}
          <div
            ref={toolAreaRef}
            key={`${slug}-${toolKey}`}
            className="space-y-12 print:space-y-0 print:m-0"
          >
            <ToolRenderer slug={slug || ''} />
          </div>

          {resultReady && (
            <ToolResultCta
              copy={copy}
              isSignedIn={isSignedIn}
              onPrimary={() => handlePrimaryCta('result_inline')}
              onSaveDraft={handleSaveDraft}
              onBrowse={() => navigate('/tools')}
            />
          )}

          {showExportFollowUp && (
            <ToolExportFollowUp
              toolTitle={tool.title}
              toolSlug={tool.slug}
              copy={copy}
              onDismiss={() => setShowExportFollowUp(false)}
            />
          )}

          {/* Action Bar */}
          <div className="mt-8 pt-8 border-t-[4px] border-black dark:border-white print:hidden">
            <div className="flex flex-col md:flex-row items-center gap-4 bg-slate-50 dark:bg-[#0A0A0A] border-[4px] border-black dark:border-white p-6 shadow-[8px_8px_0px_#000]">
              <div className="flex-1 space-y-2">
                <h3 className="text-2xl font-black font-display uppercase tracking-tighter text-black dark:text-white">
                  {isOperatorTool ? 'Keep this result, not just this tab' : 'Save or share this result'}
                </h3>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  {isOperatorTool
                    ? 'Save a draft on this device, copy a link with your inputs, or create a free workspace for your organization.'
                    : 'Save a draft on this device, copy a link with your settings, or print your result.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto">
                {isOperatorTool && (
                  <button
                    onClick={() => handlePrimaryCta('action_bar')}
                    className="bg-[#0044FF] hover:bg-blue-700 text-white border-[4px] border-black font-black uppercase tracking-widest px-5 py-3.5 text-xs transition-colors shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <ArrowRight size={16} strokeWidth={3} /> {copy.primaryCta}
                  </button>
                )}
                <button
                  onClick={handleSaveDraft}
                  className="bg-[#FFD600] text-black border-[4px] border-black font-black uppercase tracking-widest px-5 py-3.5 text-xs hover:bg-[#E6C200] transition-colors shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  onClick={handleShareLink}
                  className="bg-white text-black border-[4px] border-black font-black uppercase tracking-widest px-5 py-3.5 text-xs hover:bg-gray-100 transition-colors shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Link2 size={16} /> Copy Link
                </button>
                <button
                  onClick={handleExportClick}
                  className="bg-white text-black border-[4px] border-black font-black uppercase tracking-widest px-5 py-3.5 text-xs hover:bg-gray-100 transition-colors shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Printer size={16} /> Print / Export PDF
                </button>
              </div>
            </div>
            {hasDraft && (
              <p className="mt-3 text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 flex items-center gap-2">
                <Download size={12} /> Saved on this device — no login required.
              </p>
            )}
          </div>

          {/* FAQ Section */}
          <div className="mt-24 border-t-[8px] border-black dark:border-white pt-16 print:hidden max-w-4xl">
            <h2 className="text-4xl font-black font-display uppercase tracking-tighter mb-8 text-black dark:text-white">
              Frequently Asked Questions
            </h2>
            <div className="space-y-8">
              {faqs.map((faq, idx) => (
                <div key={idx}>
                  <h3 className="text-xl font-black text-black dark:text-white mb-2 uppercase">{faq.q}</h3>
                  <p className="font-bold text-gray-700 dark:text-gray-300">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* OrgSets Promotion / Advertisement */}
          <div className="mt-24 w-full print:hidden">
            <div className="bg-[#0044FF] border-[8px] border-black dark:border-white p-8 md:p-12 text-white relative overflow-hidden shadow-[16px_16px_0px_#000] dark:shadow-[16px_16px_0px_#fff] rounded-none">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-pink-500 rounded-none border-[8px] border-black pointer-events-none" />
              <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-[#FFD600] rounded-none border-[8px] border-black pointer-events-none" />
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="space-y-4 max-w-xl text-black">
                  <span className="inline-block bg-black text-white px-4 py-2 font-black uppercase tracking-widest text-[10px] border-[4px] border-black dark:border-white">
                    Built by OrgSets
                  </span>
                  <h3 className="text-4xl md:text-5xl font-black font-display uppercase tracking-tighter text-white drop-shadow-[4px_4px_0px_#000]">
                    {adCopy.title}
                  </h3>
                  <p className="text-white font-bold text-lg leading-relaxed drop-shadow-[2px_2px_0px_#000]">
                    {adCopy.desc}
                  </p>
                  {seoData?.ctaBullets && (
                    <ul className="mt-6 space-y-3 pt-4 border-t-4 border-black dark:border-white">
                      {seoData.ctaBullets.map((bullet, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-white font-black drop-shadow-[2px_2px_0px_#000]">
                          <CheckSquare className="text-[#FFD600] shrink-0 mt-0.5" size={24} />
                          <span className="text-lg leading-snug">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full md:w-auto flex flex-col gap-3 items-center">
                  <button
                    onClick={isOperatorTool ? handlePromoSignup : () => navigate('/tools')}
                    className="bg-white text-black border-[4px] border-black font-black uppercase tracking-widest px-8 py-4 text-lg hover:bg-[#FFD600] transition-colors shadow-[8px_8px_0px_#000]"
                  >
                    {isOperatorTool ? 'Create Free Organization' : 'Browse Free Tools'}
                  </button>
                  <span className="text-[10px] font-black uppercase tracking-widest text-white drop-shadow-[2px_2px_0px_#000] text-center block mt-2">
                    {isOperatorTool ? 'Free for up to 50 members. No credit card.' : '100+ free tools. No login required.'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="print:hidden">
        <PublicFooter />
      </div>

      {/* Sticky Bottom Conversion Bar */}
      {isOperatorTool && showStickyBar && !stickyBarDismissed && (
        <div className="fixed bottom-0 left-0 right-0 z-50 print:hidden">
          <div className="bg-black dark:bg-white border-t-4 border-[#FFD600] px-6 py-3 flex items-center justify-between gap-4 max-w-full">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Zap className="w-5 h-5 text-[#FFD600] shrink-0" />
              <p className="text-sm font-bold text-white dark:text-black truncate">
                <span className="hidden sm:inline font-extrabold mr-1.5">{copy.stickyLead}</span>
                {copy.stickyBody}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => handlePrimaryCta('sticky')}
                className="bg-[#FFD600] text-black border-2 border-black font-black uppercase tracking-wider px-4 py-2 text-xs hover:bg-[#E6C200] transition-colors shadow-[2px_2px_0px_#000] cursor-pointer whitespace-nowrap"
              >
                {copy.stickyCta}
              </button>
              <button
                onClick={dismissStickyBar}
                className="text-white/50 dark:text-black/50 hover:text-white dark:hover:text-black transition-colors cursor-pointer p-1"
                aria-label="Dismiss"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workspace / save modal */}
      <ToolClaimOrgModal 
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        toolTitle={tool.title}
        toolSlug={slug}
        toolCategory={tool.category}
      />
    </div>
  );
}
